import React, { useRef, useMemo, Suspense } from 'react';
import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import { TextureLoader, SRGBColorSpace, DoubleSide, Vector3, CanvasTexture, Shape, ExtrudeGeometry } from 'three';

// Module-level reduced-motion flag — evaluated once. When set, all in-canvas
// motion loops freeze (receipts hold their seed pose, iPad keeps its base
// rotation). HeroSection's GSAP scroll timeline is unaffected.
const REDUCED_MOTION =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Hero3DScene — pure R3F (no drei) cinematic scene.
 *
 * v2 polish:
 *  - iPad as the visual hero (centered-right, slightly larger, recessed screen to fix Z-fighting)
 *  - Receipts pushed further back, smaller, more papers but lighter
 *  - Coins glossier (higher metalness, emissive sheen)
 *  - All side coins repositioned so none clip the viewport
 *  - Stronger key-light from upper-right (gold) + rim light from below (cool)
 */

function Receipt({ position, rotation, scale = 1, accent = false, drift = 1 }) {
  const ref = useRef();
  useFrame((state, dt) => {
    if (!ref.current || REDUCED_MOTION) return;
    ref.current.rotation.z += dt * 0.04 * drift;
    ref.current.position.y = position[1] + Math.sin(state.clock.elapsedTime * 0.55 + position[0]) * 0.16;
    ref.current.position.x = position[0] + Math.cos(state.clock.elapsedTime * 0.32 + position[1]) * 0.07;
  });
  return (
    <group ref={ref} position={position} rotation={rotation} scale={scale}>
      {/* Paper body */}
      <mesh>
        <planeGeometry args={[0.62, 1.05, 4, 12]} />
        <meshStandardMaterial
          color={accent ? '#E8D29A' : '#F4ECDB'}
          roughness={0.92}
          metalness={0.02}
          side={DoubleSide}
          transparent
          opacity={accent ? 0.85 : 0.78}
        />
      </mesh>
      {/* 5 subtle horizontal "line items" — visible only at close range, hint of structure */}
      {[0.35, 0.18, 0.01, -0.16, -0.33].map((y, i) => (
        <mesh key={i} position={[0, y, 0.001]}>
          <planeGeometry args={[0.46, 0.02]} />
          <meshBasicMaterial color={accent ? '#9C7F45' : '#A89A77'} transparent opacity={0.4} />
        </mesh>
      ))}
      {/* Header bar (slightly bolder) */}
      <mesh position={[0, 0.48, 0.001]}>
        <planeGeometry args={[0.5, 0.025]} />
        <meshBasicMaterial color={accent ? '#7A5F2E' : '#8B7C5E'} transparent opacity={0.55} />
      </mesh>
    </group>
  );
}

function IPad() {
  const ref = useRef();
  // Hide the iPad on mobile — only the floating receipts background renders
  // there. Hooks below still run unconditionally to keep React happy, but the
  // mesh tree returns null.
  const { size } = useThree();
  const isMobile = size.width < 768;
  const tex = useLoader(TextureLoader, '/landing/dashboard-mockup.png');
  tex.colorSpace = SRGBColorSpace;
  tex.anisotropy = 16;

  // iPad chassis dims (slim modern Pro proportions)
  const W = 3.0;
  const H = 2.05;
  const D = 0.075;
  const RADIUS = 0.12;
  const SCREEN_W = 2.78;
  const SCREEN_H = 1.83;

  // Build a rounded-rectangle Shape and extrude it into a slim chassis. This
  // gives us proper rounded corners + a real 3D thickness — without drei's
  // RoundedBox (which leaks visual-edit props into the inner mesh and breaks R3F).
  const chassisGeo = useMemo(() => {
    const s = new Shape();
    const w = W / 2;
    const h = H / 2;
    const r = RADIUS;
    s.moveTo(-w + r, -h);
    s.lineTo(w - r, -h);
    s.quadraticCurveTo(w, -h, w, -h + r);
    s.lineTo(w, h - r);
    s.quadraticCurveTo(w, h, w - r, h);
    s.lineTo(-w + r, h);
    s.quadraticCurveTo(-w, h, -w, h - r);
    s.lineTo(-w, -h + r);
    s.quadraticCurveTo(-w, -h, -w + r, -h);
    return new ExtrudeGeometry(s, { depth: D, bevelEnabled: true, bevelThickness: 0.012, bevelSize: 0.012, bevelSegments: 4, curveSegments: 24 });
  }, []);

  // Inner "ink black" screen bezel — same rounded shape, slightly inset
  const innerBezelGeo = useMemo(() => {
    const s = new Shape();
    const w = (SCREEN_W + 0.08) / 2;
    const h = (SCREEN_H + 0.08) / 2;
    const r = 0.06;
    s.moveTo(-w + r, -h);
    s.lineTo(w - r, -h);
    s.quadraticCurveTo(w, -h, w, -h + r);
    s.lineTo(w, h - r);
    s.quadraticCurveTo(w, h, w - r, h);
    s.lineTo(-w + r, h);
    s.quadraticCurveTo(-w, h, -w, h - r);
    s.lineTo(-w, -h + r);
    s.quadraticCurveTo(-w, -h, -w + r, -h);
    return new ExtrudeGeometry(s, { depth: 0.002, bevelEnabled: false, curveSegments: 18 });
  }, []);

  // Procedural soft drop-shadow texture
  const shadowTex = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 256;
    const ctx = canvas.getContext('2d');
    const g = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    g.addColorStop(0, 'rgba(0,0,0,0.78)');
    g.addColorStop(0.45, 'rgba(0,0,0,0.32)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 256, 256);
    const t = new CanvasTexture(canvas);
    t.colorSpace = SRGBColorSpace;
    return t;
  }, []);

  // The scene is hidden entirely on mobile (HeroSection uses `hidden md:block`),
  // so this component always renders at desktop pose.
  const groupPosition = [2.45, 0.05, -0.35];
  const groupRotation = [0.05, -0.36, -0.09];
  const groupScale = 1;

  // Honour prefers-reduced-motion — disables all scroll-driven tilt and keeps
  // the device locked at its base pose.
  const reducedMotion = useMemo(
    () => (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches),
    []
  );

  // Scroll-linked tilt: as the user scrolls past the hero, the iPad gently
  // rotates AWAY (rotateX accumulates, rotateY eases slightly). Damped via
  // per-frame lerp so the motion feels sprung, not snappy.
  useFrame(() => {
    if (!ref.current) return;
    if (reducedMotion) {
      ref.current.rotation.x = groupRotation[0];
      ref.current.rotation.y = groupRotation[1];
      ref.current.position.y = groupPosition[1];
      return;
    }
    const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    const progress = Math.min(Math.max(scrollY / vh, 0), 1.2);

    const targetX = groupRotation[0] + progress * 0.18;
    const targetY = groupRotation[1] - progress * 0.08;

    const k = 0.055;
    ref.current.rotation.x += (targetX - ref.current.rotation.x) * k;
    ref.current.rotation.y += (targetY - ref.current.rotation.y) * k;
    ref.current.position.y += (groupPosition[1] - ref.current.position.y) * k;
  });

  // Z positions (extrude builds the box from z=0 outward in +Z)
  const FRONT = D + 0.024; // ~ front face including bevel

  if (isMobile) return null;

  return (
    <group ref={ref} position={groupPosition} rotation={groupRotation} scale={groupScale}>
      {/* Deep soft drop shadow plane below the iPad */}
      <mesh position={[0.1, -1.4, -0.1]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={-1}>
        <planeGeometry args={[6.2, 3.8]} />
        <meshBasicMaterial map={shadowTex} transparent depthWrite={false} opacity={1.0} />
      </mesh>

      {/* Outer chassis — silver-white aluminium rounded body */}
      <mesh geometry={chassisGeo} position={[0, 0, -D / 2 - 0.012]}>
        <meshStandardMaterial color="#E8E8E6" metalness={0.85} roughness={0.32} />
      </mesh>

      {/* Inner black screen bezel — gives the panel a proper ink-black surround */}
      <mesh geometry={innerBezelGeo} position={[0, 0, FRONT - D / 2 - 0.012 + 0.0005]}>
        <meshStandardMaterial color="#0a0a0c" metalness={0.2} roughness={0.85} />
      </mesh>

      {/* Screen — dashboard texture */}
      <mesh position={[0, 0, FRONT - D / 2 - 0.012 + 0.004]}>
        <planeGeometry args={[SCREEN_W, SCREEN_H]} />
        <meshBasicMaterial map={tex} toneMapped={false} />
      </mesh>

      {/* Glass sheen overlay */}
      <mesh position={[0, 0, FRONT - D / 2 - 0.012 + 0.0048]}>
        <planeGeometry args={[SCREEN_W, SCREEN_H]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={0.04} />
      </mesh>

      {/* Front camera dot — top centre bezel */}
      <mesh position={[0, SCREEN_H / 2 + 0.06, FRONT - D / 2 - 0.012 + 0.005]}>
        <circleGeometry args={[0.018, 24]} />
        <meshStandardMaterial color="#050507" metalness={0.6} roughness={0.3} />
      </mesh>
      <mesh position={[0, SCREEN_H / 2 + 0.06, FRONT - D / 2 - 0.012 + 0.0056]}>
        <circleGeometry args={[0.008, 16]} />
        <meshBasicMaterial color="#2a3540" />
      </mesh>

      {/* Right-side power button (along chassis edge) */}
      <mesh position={[W / 2 - 0.4, H / 2 + 0.004, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.018, 0.018, 0.18, 18]} />
        <meshStandardMaterial color="#BEBEBC" metalness={0.9} roughness={0.28} />
      </mesh>
      {/* Right-side volume buttons */}
      <mesh position={[W / 2 - 0.85, H / 2 + 0.004, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.016, 0.016, 0.13, 18]} />
        <meshStandardMaterial color="#BEBEBC" metalness={0.9} roughness={0.28} />
      </mesh>
      <mesh position={[W / 2 - 1.05, H / 2 + 0.004, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.016, 0.016, 0.13, 18]} />
        <meshStandardMaterial color="#BEBEBC" metalness={0.9} roughness={0.28} />
      </mesh>

      {/* Bottom-edge USB-C port */}
      <mesh position={[0, -H / 2 - 0.004, 0]}>
        <boxGeometry args={[0.18, 0.025, 0.04]} />
        <meshStandardMaterial color="#3a3a3c" metalness={0.6} roughness={0.5} />
      </mesh>
    </group>
  );
}

function CameraParallax() {
  const target = useMemo(() => new Vector3(), []);
  useFrame((state) => {
    target.set(state.pointer.x * 0.45, 0.25 + state.pointer.y * 0.28, 4.6);
    state.camera.position.lerp(target, 0.045);
    state.camera.lookAt(0.5, 0, 0);
  });
  return null;
}

function FloatingReceipts({ papers }) {
  // On mobile, only a curated subset of receipts is rendered AND each one uses
  // a mobile-specific position (`mp`) tuned for the narrow viewport. Fewer
  // elements keeps the phone GPU happy and the closer-to-centre positions
  // ensure the receipts actually fall inside the visible frustum.
  const { size } = useThree();
  const isMobile = size.width < 768;
  const list = isMobile ? papers.filter((p) => p.mobile) : papers;
  return (
    <>
      {list.map((p) => (
        <Receipt
          key={p.id}
          position={isMobile && p.mp ? p.mp : p.p}
          rotation={p.r}
          scale={isMobile ? p.s * 0.75 : p.s}
          accent={p.accent}
          drift={p.drift}
        />
      ))}
    </>
  );
}

export function Hero3DScene() {
  // Each receipt is tagged `mobile: true` if it should also appear on a
  // narrow viewport, with a `mp` override for its mobile world position.
  // Mobile horizontal frustum at the camera FOV is roughly ±0.8 world units,
  // so the mobile X is clamped to ±0.6.
  const papers = useMemo(() => ([
    { p: [-2.6, 1.0, -0.6],  r: [0.4, 0.2, -0.3],   s: 0.6,  drift: 1,  mobile: true,  mp: [-0.62, 1.32, -0.6] },
    { p: [-2.2, -1.05, -0.5],r: [-0.2, 0.4, 0.4],   s: 0.55, drift: -1, accent: true,  mobile: true, mp: [-0.55, -1.28, -0.5] },
    { p: [-2.9, 0.0, -0.9],  r: [0.1, -0.3, 0.2],   s: 0.5,  drift: 1 },
    { p: [-1.3, 1.5, -1.1],  r: [0.5, 0.3, 0.1],    s: 0.48, drift: 1 },
    { p: [-3.2, -1.5, -1.3], r: [0.2, -0.6, 0.3],   s: 0.5,  drift: -1 },
    { p: [3.6, 1.3, -1.5],   r: [-0.3, 0.1, -0.45], s: 0.5,  drift: 1, accent: true,  mobile: true, mp: [0.6, 1.35, -1.0] },
    { p: [3.4, -1.5, -1.4],  r: [0.2, 0.5, 0.1],    s: 0.45, drift: -1, mobile: true, mp: [0.58, -1.42, -1.0] },
  ].map((q, i) => ({ ...q, id: i }))), []);

  return (
    <Canvas
      dpr={[1, 1.8]}
      camera={{ position: [0, 0.25, 4.6], fov: 36 }}
      gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
    >
      <color attach="background" args={['#28282B']} />
      <fog attach="fog" args={['#28282B', 5.5, 11]} />

      {/* Studio-style 3-point lighting */}
      <ambientLight intensity={0.42} />
      {/* Key light: warm gold from upper-right */}
      <directionalLight position={[5, 4, 5]} intensity={1.4} color="#FFD580" />
      {/* Fill light: cool emerald from lower-left */}
      <directionalLight position={[-4, -2, 3]} intensity={0.6} color="#A8D5BA" />
      {/* Rim light: subtle gold backlight */}
      <pointLight position={[2, 1, -2]} intensity={0.5} color="#E8D29A" />
      <pointLight position={[-2, 0, 2]} intensity={0.35} color="#FFEBC0" />

      <Suspense fallback={null}>
        <IPad />
        <FloatingReceipts papers={papers} />
      </Suspense>

      <CameraParallax />
    </Canvas>
  );
}

export default Hero3DScene;
